import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { deleteMixVersion, getMix, renderMix, saveMix } from "@/lib/mixDeskApi";
import { safeApiError } from "@/lib/safeErrorText";

const SAVE_MS = 800;
const POLL_MS = 2500;

/** Mix Desk state: the saved settings (auto-saved as you move things), the render job, and the numbered mixes. */
export default function useMixDesk(songId) {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(null);
  const [saveState, setSaveState] = useState("saved");
  const [starting, setStarting] = useState(false);
  const timer = useRef(null);
  const latest = useRef(null);
  const lastStatus = useRef(null);
  latest.current = settings;

  const load = useCallback(async (adoptSettings = false) => {
    try {
      const d = await getMix(songId);
      const was = lastStatus.current;
      lastStatus.current = d.render?.status;
      if (was === "running" && d.render?.status === "done") toast.success(`Mix ${d.render.number} is ready`, { description: "Saved below and in your Songs as WAV + MP3." });
      if (was === "running" && d.render?.status === "error") toast.error("The mix didn't finish", { description: d.render.error });
      setData(d);
      if (adoptSettings) setSettings(d.settings);
    } catch (e) { setError(e?.response?.status === 404 ? "We couldn't find that song." : safeApiError(e)); }
  }, [songId]);
  useEffect(() => { load(true); }, [load]);

  const running = data?.render?.status === "running";
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => load(false), POLL_MS);
    return () => clearInterval(t);
  }, [running, load]);

  const persist = useCallback(async () => {
    clearTimeout(timer.current);
    timer.current = null;
    try { await saveMix(songId, latest.current); setSaveState("saved"); }
    catch (e) { setSaveState("error"); toast.error("Couldn't save the mix", { description: safeApiError(e) }); }
  }, [songId]);
  useEffect(() => () => { if (timer.current) { clearTimeout(timer.current); saveMix(songId, latest.current).catch(() => {}); } }, [songId]);

  const change = useCallback((fn) => {
    setSettings((s) => { const next = fn(s); latest.current = next; return next; });
    setSaveState("saving");
    clearTimeout(timer.current);
    timer.current = setTimeout(persist, SAVE_MS);
  }, [persist]);

  const updateTrack = useCallback((name, patch) => change((s) => ({ ...s, tracks: { ...s.tracks, [name]: { ...s.tracks[name], ...patch } } })), [change]);
  const updateMaster = useCallback((patch) => change((s) => {
    if (patch.preset) {
      const p = data.presets.find((x) => x.key === patch.preset);
      return { ...s, master: { preset: p.key, lufs: p.lufs, warmth: p.warmth, brightness: p.brightness, width: p.width, ceiling: p.ceiling } };
    }
    return { ...s, master: { ...s.master, ...patch } };
  }), [change, data]);
  const update = useCallback((patch) => change((s) => ({ ...s, ...patch })), [change]);
  const adopt = useCallback((next) => change(() => next), [change]);

  const reset = useCallback(async () => {
    try { const r = await saveMix(songId, { take_id: latest.current?.take_id }); setSettings(r.settings); setSaveState("saved"); toast.message("Mix reset to the starting balance"); }
    catch (e) { toast.error("Couldn't reset", { description: safeApiError(e) }); }
  }, [songId]);

  const render = useCallback(async () => {
    setStarting(true);
    clearTimeout(timer.current);
    timer.current = null;
    try {
      const r = await renderMix(songId, latest.current);
      setSaveState("saved");
      lastStatus.current = r.render?.status;
      setData((d) => ({ ...d, render: r.render }));
    } catch (e) { toast.error("Couldn't start the mix", { description: safeApiError(e) }); }
    finally { setStarting(false); }
  }, [songId]);

  const removeVersion = useCallback(async (v) => {
    try { const r = await deleteMixVersion(songId, v.id); setData((d) => ({ ...d, versions: r.versions })); toast.message(`Mix ${v.number} removed`); }
    catch (e) { toast.error("Couldn't remove it", { description: safeApiError(e) }); }
  }, [songId]);

  return { data, settings, error, saveState, starting, running, updateTrack, updateMaster, update, adopt, reset, render, removeVersion };
}
